// -------------------------------------------------------------------------------------------------
// 1. Import Components
import "./reset_password.mcss";
import "@bux/input/text/string.mts";
import "@bux/component/password_creator.mts";

// -------------------------------------------------------------------------------------------------
// 2. Import Code
import { SEC } from "@arlib/lib.mts";
import { type Option, Uuid } from "@artyp/lib.mts";
import { copy_view, make_view } from "@bux/document/mod.mts";
import { initiate_reset, update_password, verify_code } from "./reset_passwordλ.mts";

import BuxComponentPasswordCreator from "@bux/component/password_creator.mts";
import BuxInputTextString from "@bux/input/text/string.mts";

// -------------------------------------------------------------------------------------------------
// 3. Find Elements

const $views = SEC(HTMLDivElement, document, "#views");

// -------------------------------------------------------------------------------------------------
// 4. Define States

function enter_email_state() {
    const $view = copy_view("view1");
    const $form = SEC(HTMLFormElement, $view, "form");
    const $email = SEC(BuxInputTextString, $view, "[name=email]");

    $form.addEventListener("submit", async (event: Event) => {
        event.preventDefault();
        await submit();
    });

    $views.replaceChildren($view);

    async function submit() {
        $views.replaceChildren(make_view(
            "processing",
            `
            <p>Processing...</p>
        `,
        ));

        const email = $email.value ?? null;
        // Call server
        const output = await initiate_reset.call({
            email,
        });

        if ("Error" in output) {
            $views.replaceChildren($view);
            $email.set_e(output.Error.message);
            $form.reportValidity();
            return;
        }

        if ("Disabled" in output) {
            $views.replaceChildren($view);
            $email.set_e("Password reset is currently disabled.");
            $form.reportValidity();
            return;
        }

        return enter_code_state({
            reset_password_uuid: output.Ok.reset_password_uuid,
            from_address: output.Ok.from_address,
            from_name: output.Ok.from_name,
            subject: output.Ok.subject,
        });
    }
}

function enter_code_state(
    trans: { reset_password_uuid: Uuid; from_address: string; from_name: Option<string>; subject: string; },
) {
    const $view = copy_view("view2");
    const $form = SEC(HTMLFormElement, $view, "form");
    const $code = SEC(BuxInputTextString, $view, "[name=code]");
    const $email_info = SEC(HTMLElement, $view, "email-sent-info");
    $email_info.textContent = [
        `From: ${trans.from_name ?? "—"} <${trans.from_address}>`,
        `Subject: ${trans.subject}`,
    ].join("\n");

    $form.addEventListener("submit", async (event: Event) => {
        event.preventDefault();
        await submit();
    });

    $views.replaceChildren($view);

    async function submit() {
        $views.replaceChildren(make_view(
            "processing",
            `
            <p>Processing...</p>
        `,
        ));

        const code = $code.value ?? null;
        // Call server
        const output = await verify_code.call({
            reset_password_uuid: trans.reset_password_uuid,
            code,
        });

        if ("InputErr" in output) {
            $views.replaceChildren($view);
            $code.set_e(output.InputErr[0].code ?? null);
            $form.reportValidity();
            return;
        }

        if ("Disabled" in output) {
            $views.replaceChildren($view);
            $code.set_e("Password reset is currently disabled.");
            $form.reportValidity();
            return;
        }

        return new EnterPasswordState({ reset_password_uuid: trans.reset_password_uuid });
    }
}

class EnterPasswordState {
    $view = copy_view("view3");
    $view_processing = make_view(
        "processing",
        `
        <p>Processing...</p>
    `,
    );
    $form = SEC(HTMLFormElement, this.$view, "form");
    $password_creator = SEC(BuxComponentPasswordCreator, this.$view, "bux-component-password-creator");
    reset_password_uuid: Uuid;

    constructor(trans: { reset_password_uuid: Uuid; }) {
        this.reset_password_uuid = trans.reset_password_uuid;

        this.$form.addEventListener("submit", async (event: Event) => {
            event.preventDefault();
            await this.submit();
        });

        $views.replaceChildren(this.$view);
    }

    async submit() {
        $views.replaceChildren(this.$view_processing);
        const password = this.$password_creator.value ?? null;
        // Call server
        const output = await update_password.call({
            reset_password_uuid: this.reset_password_uuid,
            password,
        });

        if ("InputErr" in output) {
            $views.replaceChildren(this.$view);
            this.$password_creator.set_e(output.InputErr[0].password ?? undefined);
            this.$form.reportValidity();
            return;
        }

        if ("Disabled" in output) {
            $views.replaceChildren(this.$view);
            this.$password_creator.set_e("Password reset is currently disabled.");
            this.$form.reportValidity();
            return;
        }

        return complete_state();
    }
}

function complete_state() {
    const $view = copy_view("view4");
    $views.replaceChildren($view);
}

enter_email_state();
